import React, { useEffect, useState } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import DatePicker from "react-datepicker";
import { useSelector } from "react-redux";
import { roleCheck } from "utils/index";

import { isPast } from "date-fns";
import moment from "moment";
import { toast } from "react-toastify";
import { updateSurveyorAssignmentDate, getSameLocationTimeJobServices } from "requesters/jobServiceRequester";
import { deleteScheduledAssignmentGroup } from "requesters/surveyorAssignmentRequester";
import MoveMultipleJobServicesModal from "../../modals/MoveMultipleJobServicesModal";
import { getBaseUrl } from "../../../../reducers";
import {
  createDateFilter,
  getMinSelectableDate,
  VALIDATION_PRESETS
} from "../../../../../../utils/dateValidation";

const ShowChangeBookingDate = ({
  jobService, reloadData, systemRole, currentTeamRole, scheduledAssignment
}) => {
  const {
 attributes: {
 id, bookingTime, bookingDates, canDeleteScheduledAssignment
}
} = jobService
  const {
    scheduledAt
  } = scheduledAssignment

  const [prefBookingDate, setPrefBookingDate] = useState(scheduledAt ? new Date(scheduledAt) : null)

  const baseUrl = useSelector(getBaseUrl)
  const assignmentId = scheduledAssignment.primarySurveyorAssignmentId;
  const [showUpdate, setShowUpdate] = useState(false)
  const [showMoveForwardConfirmation, setShowMoveForwardConfirmation] = useState(false)
  const [allowMoveForward, setAllowMoveForward] = useState(false)

  const [showMessage, setShowMessage] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [alertTitle, setAlertTitle] = useState("")
  const [alertText, setAlertText] = useState("")
  const [outsideHoursWarning, setOutsideHoursWarning] = useState(null)
  const [sameLocationTimeJobServices, setSameLocationTimeJobServices] = useState([])
  const [showMoveMultipleModal, setShowMoveMultipleModal] = useState(false)
  const [pendingUpdateParams, setPendingUpdateParams] = useState(null)

  const resetError = () => {
    setShowMessage(false)
    setAlertTitle("")
    setAlertText("")
  }

  const extractErrorMessage = res => {
    const errors = res?.error?.response?.data?.errors
    if (Array.isArray(errors) && errors.length > 0) {
      // Check all errors for "Outside of Work Hours" message
      const outsideHoursError = errors.find(err => {
        const errorText = typeof err === "string" ? err : (err?.detail || err?.title || "")
        return typeof errorText === "string" && errorText.includes("Outside of Work Hours")
      })
      
      if (outsideHoursError) {
        return typeof outsideHoursError === "string" ? outsideHoursError : (outsideHoursError?.detail || outsideHoursError?.title || "")
      }
      
      // If no "Outside of Work Hours" error found, return first error
      const firstError = errors[0]
      if (typeof firstError === "string") return firstError
      if (firstError?.detail) return firstError.detail
      if (firstError?.title) return firstError.title
    }

    if (Array.isArray(res?.error?.response?.data) && res.error.response.data.length > 0) {
      return res.error.response.data[0]
    }

    return res?.error?.response?.data?.error || "Something went wrong. Please try again."
  }

  const toastOptions = {
    toastId: "container-inner",
    containerId: "main"
  }

  const deleteAssignmentHandler = () => {
    setShowDeleteConfirmation(true);
  }

  const deleteAssignment = () => {
    deleteScheduledAssignmentGroup(baseUrl, assignmentId)
      .then(res => {
        if (res.status === 200) {
          reloadData();
          setShowDeleteConfirmation(false)
        }
      })
      .catch(err => console.log(err));
  }

  const updateBookingDateWithParams = async (allowOutsideHours = false, additionalJobServiceIds = []) => {
    const params = {
      booking_time: scheduledAt,
      new_booking_time: prefBookingDate,
      ...(allowOutsideHours && { allow_outside_work_hours: true }),
      ...(additionalJobServiceIds.length > 0 && { additional_job_service_ids: additionalJobServiceIds })
    }

    updateSurveyorAssignmentDate(baseUrl, id, params)
      .then(res => {
        if (res.error) {
          // Handle error from API
          const errorMessage = extractErrorMessage(res)

          // Check if it's an "Outside of Work Hours" error
          if (typeof errorMessage === "string" && errorMessage.includes("Outside of Work Hours")) {
            setOutsideHoursWarning(errorMessage)
          } else {
            setShowMessage(true)
            setAlertTitle("Error updating booking date")
            setAlertText(errorMessage || "Failed to update booking date. Please try again.")
          }
        } else if (res.status === 200) {
          setShowUpdate(false)
          setAllowMoveForward(false)
          setOutsideHoursWarning(null)
          setPendingUpdateParams(null)
          reloadData()
        }
      })
      .catch(err => {

        // Fallback for unexpected errors
        const errorMessage = err?.response?.data?.errors?.[0]?.detail ||
                           err?.response?.data?.error ||
                           err?.message ||
                           "Failed to update booking date. Please try again."

        if (typeof errorMessage === "string" && errorMessage.includes("Outside of Work Hours")) {
          setOutsideHoursWarning(errorMessage)
        } else {
          setShowMessage(true)
          setAlertTitle("Error updating booking date")
          setAlertText(errorMessage)
        }
      })
  }

  const editBookingTimeHandler = async () => {
    if (!isPast(prefBookingDate)) {
      // Filter out the current scheduledAt date to only check conflicts with other booking dates
      const otherBookingDates = bookingDates.filter(date => !moment(date).isSame(scheduledAt, "day"))
      const isNewBookingDateExisted = otherBookingDates.some(date => moment(date).isSame(prefBookingDate, "day"))

      if (isNewBookingDateExisted && !allowMoveForward) {
        setShowMoveForwardConfirmation(true)
      } else {
        // Check for same location/time job services at the CURRENT booking time
        // We want to find other jobs at the same location and current time that should be moved together
        // Send the time in ISO format so the backend can parse it correctly with timezone info
        const formattedCurrentTime = moment(scheduledAt).format()
        const result = await getSameLocationTimeJobServices(baseUrl, id, { booking_time: formattedCurrentTime })

        // The API response structure is: { response: { json: { job_services: [...] } }, status: 200 }
        const jobServices = result.response?.json?.job_services || result.response?.job_services || []

        if (result.status === 200 && jobServices.length > 0) {
          // Show confirmation modal
          setSameLocationTimeJobServices(jobServices)
          setPendingUpdateParams({ allowOutsideHours: false })
          setShowMoveMultipleModal(true)
        } else {
          // No other jobs, proceed with update
          updateBookingDateWithParams(false, [])
        }
      }
    } else if (isPast(prefBookingDate)) {
        setShowMessage(true)
        setAlertTitle("Please choose an available timeslot!")
        setAlertText("You can only choose a timeslot in the future!")
        setPrefBookingDate(new Date(bookingTime))
      }
  }

  const handleMoveAllConfirm = () => {
    setShowMoveMultipleModal(false)
    const additionalIds = sameLocationTimeJobServices.map(js => js.job_service_id)
    updateBookingDateWithParams(pendingUpdateParams?.allowOutsideHours || false, additionalIds)
  }

  const handleMoveOnlyCurrentConfirm = () => {
    setShowMoveMultipleModal(false)
    updateBookingDateWithParams(pendingUpdateParams?.allowOutsideHours || false, [])
  }

  useEffect(() => {
    if (allowMoveForward) {
      setShowMoveForwardConfirmation(false)
      editBookingTimeHandler()
    }
  }, [allowMoveForward])

  // const sentJobServiceAccessEmail = () => {
  //   sendAccessEmail(baseUrl, id)
  //   .then(res => {
  //     if (res.status === 200) {
  //       // reloadData()
  //       toast.success(
  //         <div>
  //           <span role="img" aria-label="success-icon">?</span>
  //           <span className="ml-2">Email Sent!</span>
  //         </div>,
  //           toastOptions
  //       )
  //     }
  //   })
  // .catch(err => {
  //   toast.error(
  //     <div>
  //       <span role="img" aria-label="failure-icon">?</span>
  //       <span className="ml-2">{err?.response?.data?.errors[0]?.detail}</span>
  //     </div>,
  //       toastOptions
  //   )
  // })
  // }

  return (
    <>
      <div className="row mb-4">
        <div className="ca-custom">
          <DatePicker
            required
            selected={prefBookingDate}
            readOnly={!roleCheck(systemRole, currentTeamRole, "operations_manager")}
            onChange={e => {
              setPrefBookingDate(e);
              setShowUpdate(true);
            }}
            showTimeSelect
            closeOnScroll={e => e.target === document}
            className="form-control mb-2 mt-2 ml-1"
            dateFormat="dd MMM yyyy, h:mma"
            minDate={getMinSelectableDate()}
            filterDate={createDateFilter(VALIDATION_PRESETS.BOOKING_DATES)} />
          <div className="ml-2 pt-2">
            {showUpdate && !scheduledAssignment.anySurveyorCheckedIn && (
              <button type="button" className="btn btn-outline-secondary btn-md mr-2" onClick={() => editBookingTimeHandler()}>
                Update
              </button>
            )}
            {/* <button className="btn btn-outline-primary" type="button" onClick={sentJobServiceAccessEmail}>Send Confirmation Email</button> */}
          </div>
        </div>

        <SweetAlert
          show={showMessage}
          type="error"
          title={alertTitle}
          closeOnClickOutside
          allowEscape={false}
          onConfirm={resetError}>
          {alertText}
        </SweetAlert>
        <SweetAlert
          show={showMoveForwardConfirmation}
          type="info"
          title="Are you sure?"
          closeOnClickOutside
          allowEscape={false}
          showCancel
          cancelBtnBsStyle="secondary"
          onCancel={() => { setShowMoveForwardConfirmation(false) }}
          onConfirm={() => setAllowMoveForward(true)}>
          The timeslot of that day already existed. Would you like to move forward the booking dates to next available day?
        </SweetAlert>
        <SweetAlert
          show={!!outsideHoursWarning}
          type="warning"
          title="Outside of Work Hours"
          showCancel
          closeOnClickOutside={false}
          allowEscape={false}
          confirmBtnText="Confirm"
          cancelBtnText="Cancel"
          onConfirm={() => {
            setOutsideHoursWarning(null)
            updateBookingDateWithParams(true)
          }}
          onCancel={() => {
            setOutsideHoursWarning(null)
          }}>
          {outsideHoursWarning?.split("\n").map((line, index) => (
            <p key={index} className="mb-1">{line}</p>
          ))}
        </SweetAlert>
      </div>

      <MoveMultipleJobServicesModal
        show={showMoveMultipleModal}
        onHide={() => setShowMoveMultipleModal(false)}
        jobServices={sameLocationTimeJobServices}
        onConfirm={handleMoveAllConfirm}
        onCancel={handleMoveOnlyCurrentConfirm} />

      <div className="row">
        <div className="ml-2">
          {(canDeleteScheduledAssignment && !scheduledAssignment.anySurveyorCheckedIn) && (
            <button type="button" className="btn btn-outline-danger btn-md mr-2" onClick={() => deleteAssignmentHandler()}>
              Delete
            </button>

          )}
        </div>
        <SweetAlert
          show={showDeleteConfirmation}
          type="info"
          title="Are you sure?"
          closeOnClickOutside
          allowEscape={false}
          onConfirm={deleteAssignment}>
          This will delete this scheduled group of assignments, are you sure?
        </SweetAlert>

      </div>
    </>
  )
}
export default ShowChangeBookingDate
